import React, { useEffect, useMemo, useState } from "react";
import api from "../utils/api";
import { useNavigate } from "react-router-dom";
import Select from "react-select";

function todayYMD() {
  return new Date().toISOString().slice(0, 10);
}

function toYMD(val) {
  const s = String(val || "").trim();
  if (!s) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const dmy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;

  return "";
}

function toDMY(val) {
  const s = String(val || "").trim();
  if (!s) return "";

  const onlyDate = s.slice(0, 10);
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(onlyDate);
  if (ymd) return `${ymd[3]}/${ymd[2]}/${ymd[1]}`;

  const dmy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (dmy) return s;

  return "";
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function cleanStr(v) {
  return String(v ?? "").trim();
}

function toArray(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

function getMachineValue(m) {
  if (!m) return "";
  if (typeof m === "string") return m;

  return (
    m.machine_name ||
    m.machineName ||
    m.machine_no ||
    m.machineNo ||
    m.mc_no ||
    m.mcNo ||
    m.name ||
    m.machine ||
    m.code ||
    m.value ||
    ""
  );
}

function getItemCode(it) {
  if (!it) return "";
  return it.item_code || it.itemCode || it.code || it.value || "";
}

function getItemName(it) {
  if (!it) return "";
  return it.item_name || it.itemName || it.name || it.label || "";
}

function getEmpCode(emp) {
  return emp?.emp_code || emp?.empCode || emp?.code || "";
}

function getEmpName(emp) {
  return emp?.emp_name || emp?.empName || emp?.name || "";
}

export default function Crush1Add() {
  const navigate = useNavigate();

  const [date, setDate] = useState(toDMY(todayYMD()));
  const [receivedBy, setReceivedBy] = useState("");

  const [machines, setMachines] = useState([]);
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [rows, setRows] = useState([
    {
      machineNo: "",
      operatorName: "",
      timeHour: "",
      itemCode: "",
      itemName: "",
      resultKg: "",
      receiveDate: "",
    },
  ]);

  const [loading, setLoading] = useState(true);

  const itemNameByCode = useMemo(() => {
    const map = new Map();
    items.forEach((it) => {
      map.set(getItemCode(it), getItemName(it) || "");
    });
    return map;
  }, [items]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);

        const [mRes, itemsRes, empRes] = await Promise.all([
          api.get("/api/machines?type=Extruder"),
          api.get("/api/items?type=recycle"),
          api.get("/api/employees"),
        ]);

        if (!alive) return;

        setMachines(toArray(mRes.data));
        setItems(toArray(itemsRes.data));
        setEmployees(toArray(empRes.data));
      } catch (e) {
        console.error("LOAD ERROR:", e);
        alert(
          e?.response?.data?.sqlMessage ||
            e?.response?.data?.message ||
            e?.response?.data?.error ||
            e?.message ||
            "Failed to load master data"
        );
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const machineOptions = useMemo(() => {
    return machines
      .map((m) => {
        const value = getMachineValue(m);
        return value ? { value, label: value } : null;
      })
      .filter(Boolean);
  }, [machines]);

  const itemCodeOptions = useMemo(() => {
    return items
      .map((it) => {
        const code = getItemCode(it);
        const name = getItemName(it);

        if (!code) return null;

        return {
          value: code,
          label: code,
          searchText: `${code} ${name}`.toLowerCase(),
        };
      })
      .filter(Boolean);
  }, [items]);

  const employeeOptions = useMemo(() => {
    return employees
      .map((emp) => {
        const empCode = getEmpCode(emp);
        const empName = getEmpName(emp);

        if (!empName) return null;

        return {
          value: empName,
          label: empName,
          searchText: `${empName} ${empCode}`.toLowerCase(),
        };
      })
      .filter(Boolean);
  }, [employees]);

  const grandTotalKg = useMemo(() => {
    return rows.reduce((s, r) => s + num(r.resultKg), 0);
  }, [rows]);

  function updateRow(i, k, v) {
    setRows((prev) => {
      const copy = [...prev];
      const row = { ...copy[i], [k]: v };

      if (k === "itemCode") {
        row.itemName = itemNameByCode.get(v) || "";
      }

      copy[i] = row;
      return copy;
    });
  }

  function addRow() {
    setRows((prev) => [
      ...prev,
      {
        machineNo: "",
        operatorName: "",
        timeHour: "",
        itemCode: "",
        itemName: "",
        resultKg: "",
        receiveDate: "",
      },
    ]);
  }

  function removeRow(i) {
    setRows((prev) =>
      prev.length === 1 ? prev : prev.filter((_, x) => x !== i)
    );
  }

  async function saveEntry() {
    try {
      const productionDate = toYMD(date);

      if (!productionDate) return alert("Valid Date required");
      if (!cleanStr(receivedBy)) return alert("Received By required");

      const lines = rows
        .map((r) => ({
          machineNo: cleanStr(r.machineNo),
          operatorName: cleanStr(r.operatorName),
          timeHour: cleanStr(r.timeHour),
          itemCode: cleanStr(r.itemCode),
          itemName: cleanStr(r.itemName),
          resultKg: num(r.resultKg),
          receiveDate: cleanStr(r.receiveDate) ? toYMD(r.receiveDate) : null,
        }))
        .filter(
          (x) =>
            x.machineNo &&
            x.operatorName &&
            x.timeHour &&
            x.itemCode &&
            x.itemName &&
            x.resultKg > 0 &&
            x.receiveDate
        );

      if (!lines.length) {
        return alert("Fill at least one complete row");
      }

      console.log("CRUSH1 SAVE PAYLOAD:", {
        productionDate,
        receivedBy: cleanStr(receivedBy),
        lines,
      });

      const res = await api.post("/api/crush/crush1", {
        productionDate,
        receivedBy: cleanStr(receivedBy),
        lines,
      });

      alert(`Saved successfully. Entry ID: ${res.data.headerId}`);
      navigate("/crush-1");
    } catch (e) {
      console.error("SAVE ERROR:", e?.response?.data || e);
      alert(
        e?.response?.data?.sqlMessage ||
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          "Save failed"
      );
    }
  }

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "31px",
      height: "31px",
      border: "1px solid #ced4da",
      boxShadow: "none",
      borderRadius: "0.25rem",
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: "31px",
      padding: "0 8px",
    }),
    input: (provided) => ({
      ...provided,
      margin: "0px",
      padding: "0px",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "31px",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "4px",
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: "4px",
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      marginTop: "6px",
      marginBottom: "6px",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      fontSize: "14px",
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  if (loading) return <div className="p-3">Loading...</div>;

  return (
    <div className="container-fluid p-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h2 className="mb-0">Daily Crush & Recycling Department Production</h2>
        <div className="d-flex gap-2">
          <button
            className="btn btn-dark"
            onClick={saveEntry}
            title="Save this Crush 1 entry"
          >
            Save
          </button>
          <button
            className="btn btn-outline-secondary"
            onClick={() => navigate("/crush-1")}
            title="Close this page and go back"
          >
            Close
          </button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body row g-3">
          <div className="col-md-3">
            <label
              className="form-label"
              title="Select production date for this Crush 1 entry"
            >
              Date
            </label>
            <input
              type="date"
              className="form-control"
              value={toYMD(date)}
              onChange={(e) => setDate(toDMY(e.target.value))}
              title="Production date"
              placeholder="Select production date"
            />
          </div>

          <div className="col-md-5">
            <label
              className="form-label"
              title="Select the employee who received this entry"
            >
              Received By
            </label>
            <Select
              options={employeeOptions}
              value={
                employeeOptions.find((opt) => opt.value === receivedBy) || null
              }
              onChange={(selected) =>
                setReceivedBy(selected ? selected.value : "")
              }
              placeholder="Select employee"
              isClearable
              isSearchable
              styles={customSelectStyles}
              noOptionsMessage={() => "No employee found"}
              filterOption={(option, inputValue) => {
                const text =
                  `${option.label} ${option.data.searchText || ""}`.toLowerCase();
                return text.includes(String(inputValue).toLowerCase());
              }}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table table-bordered align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th title="Serial number" style={{ width: 60 }}>
                  SN
                </th>
                <th title="Select machine number" style={{ width: 200 }}>
                  M/C # No.
                </th>
                <th title="Select operator name" style={{ width: 220 }}>
                  Operator Name
                </th>
                <th title="Enter time or hour" style={{ width: 160 }}>
                  Time / Hour
                </th>
                <th title="Select recycle item code" style={{ width: 220 }}>
                  Item Code (Recycle)
                </th>
                <th title="Auto-filled item name">Item Name</th>
                <th title="Enter result quantity in KG" style={{ width: 140 }}>
                  Result / KG
                </th>
                <th title="Select receive date" style={{ width: 170 }}>
                  Receive Date
                </th>
                <th title="Add or remove row" style={{ width: 180 }}>
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td className="text-center">{i + 1}</td>

                  <td title="Select machine number">
                    <Select
                      options={machineOptions}
                      value={
                        machineOptions.find((opt) => opt.value === r.machineNo) ||
                        null
                      }
                      onChange={(selected) =>
                        updateRow(i, "machineNo", selected ? selected.value : "")
                      }
                      placeholder="Select machine"
                      isClearable
                      isSearchable
                      styles={customSelectStyles}
                      noOptionsMessage={() => "No machine found"}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </td>

                  <td title="Select operator name">
                    <Select
                      options={employeeOptions}
                      value={
                        employeeOptions.find(
                          (opt) => opt.value === r.operatorName
                        ) || null
                      }
                      onChange={(selected) =>
                        updateRow(
                          i,
                          "operatorName",
                          selected ? selected.value : ""
                        )
                      }
                      placeholder="Select operator"
                      isClearable
                      isSearchable
                      styles={customSelectStyles}
                      noOptionsMessage={() => "No employee found"}
                      filterOption={(option, inputValue) => {
                        const text =
                          `${option.label} ${option.data.searchText || ""}`.toLowerCase();
                        return text.includes(String(inputValue).toLowerCase());
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </td>

                  <td title="Enter working time or hour">
                    <input
                      className="form-control form-control-sm"
                      value={r.timeHour}
                      onChange={(e) => updateRow(i, "timeHour", e.target.value)}
                      title="Enter time / hour"
                      placeholder="e.g. 8 or 08:00"
                    />
                  </td>

                  <td title="Select recycle item code">
                    <Select
                      options={itemCodeOptions}
                      value={
                        itemCodeOptions.find((opt) => opt.value === r.itemCode) ||
                        null
                      }
                      onChange={(selected) =>
                        updateRow(i, "itemCode", selected ? selected.value : "")
                      }
                      placeholder="Select item code"
                      isClearable
                      isSearchable
                      styles={customSelectStyles}
                      noOptionsMessage={() => "No item found"}
                      filterOption={(option, inputValue) => {
                        const text =
                          `${option.label} ${option.data.searchText || ""}`.toLowerCase();
                        return text.includes(String(inputValue).toLowerCase());
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </td>

                  <td title="Item name auto-filled from item code">
                    <input
                      className="form-control form-control-sm"
                      value={r.itemName}
                      readOnly
                      title="Item name"
                      placeholder="Auto-filled item name"
                    />
                  </td>

                  <td title="Enter result quantity in KG">
                    <input
                      className="form-control form-control-sm"
                      value={r.resultKg}
                      onChange={(e) => updateRow(i, "resultKg", e.target.value)}
                      title="Result quantity in KG"
                      placeholder="Enter KG (e.g. 200)"
                    />
                  </td>

                  <td title="Select receive date">
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={toYMD(r.receiveDate)}
                      onChange={(e) =>
                        updateRow(i, "receiveDate", toDMY(e.target.value))
                      }
                      title="Receive date"
                      placeholder="Select receive date"
                    />
                  </td>

                  <td>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeRow(i)}
                        title="Remove this row"
                      >
                        Remove
                      </button>
                      <button
                        className="btn btn-sm btn-outline-dark"
                        onClick={addRow}
                        title="Add new row"
                      >
                        + Add
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card-footer d-flex justify-content-between">
          <b title="Total number of rows">Total Records: {rows.length}</b>
          <b title="Sum of all result KG">
            Grand Total KG: {grandTotalKg.toFixed(2)}
          </b>
        </div>
      </div>

      <div className="text-muted mt-2" style={{ fontSize: 12 }}>
        Note: Hover over labels and fields to see tips.
      </div>
    </div>
  );
}